import { Component, computed, ContentChild, effect, ElementRef, HostListener, inject, input, OnInit, output, signal, TemplateRef, ViewChild } from '@angular/core';
import { WindowRefService } from '../../../../services/window-ref.service';
import { CookieService } from 'ngx-cookie-service';
import { Store } from '@ngrx/store';
import { AuthLinkType } from '../../../../enums/event/event-data.enum';
import { AuthEventRepo, AuthEventType } from '../../../../enums/event/event.enum';
import { LinkClickCustomerPortal } from '../../../../models/event-data.model';
import { EventTrackingService } from '../../../../services/event-tracking.service';
import { selectDismissHubOffer } from '../../../offers/store/selectors/offers.selectors';
import { isRefiOfferType, isSecondLoanOrIta, Offer, OfferType, shouldShowMinAprInTitle } from '../../../offers/models/offers.model';
import { OffersService } from '../../../offers/services/offers.service';

const DISPLAY_MOBILE_TOP_POSITION = 400;
const ZEN_DESK_LAUNCHER_ADJUSTMENT = 200;

@Component({
  selector: 'app-offers-carousel',

  templateUrl: './offers-carousel.component.html',
  styleUrl: './offers-carousel.component.scss',
  standalone: false
})
export class OffersCarouselComponent implements OnInit {
  readonly offers = input<Offer[]>([]);
  readonly isStickyOfferBannerEnabled = input<boolean>(false);
  readonly cid = input<string>();
  readonly siteSection = input<string>('hub');
  readonly closeBanner = output<boolean>();
  readonly learnMore = output<Offer>();
  readonly isRefiOfferFirst = input<boolean>(false);
  loop = true;
  showDots = computed(() => this.sortedOffers().length > 1);

  currentOffer: Offer;
  displayAsCarousel = computed(() => this.sortedOffers().length > 1);

  carouselDisplayIndex: number = 0;
  offersSectionTitle: string;
  private swipeXStart: number;
  isMobile = innerWidth <= 440 || false;
  scrolled = false;
  direction: 'up' | 'down' = 'up';
  stickyBannerEvent: string;
  sortedOffers = computed(() => {
    let offers = [...this.offers()];

    if (this.isRefiOfferFirst()) {
      offers = this.sortRefiOffersFirst(offers);
    }
    return offers;
  })
  index = signal(0);
  private parentDivChildrenOriginalBottom: Map<HTMLElement, string> = new Map();



  private readonly cookieService = inject(CookieService);
  private readonly windowRefService = inject(WindowRefService);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly offersService = inject(OffersService);
  private readonly store = inject(Store);
  readonly isBannerDismissed = this.store.selectSignal(selectDismissHubOffer);
  readonly shouldShowMinAprInTitle = shouldShowMinAprInTitle;

  @ContentChild(TemplateRef)
  slideTemplate!: TemplateRef<{ $implicit: Offer }>;
  @ViewChild('offersCarousel')
  private carouselContainer!: ElementRef;

  constructor() {
    effect(() => {
      if (this.isBannerDismissed() && this.isStickyOfferBannerEnabled()) {
        // Reset the ZenDesk launcher position when banner is dismissed
        this.updateZenDeskLauncherPosition(false);
      }
    });
  }

  ngOnInit(): void {
    this.stickyBannerEvent = this.isStickyOfferBannerEnabled() && this.isMobile ? '_STICKY_BANNER' : '';
    this.updateScrolled();

    this.selectCurrentOffer(this.carouselDisplayIndex);

    this.dropToCookie(
      'be_pl_2nd_offer_campaign',
      new URL(this.currentOffer.offerUrl).searchParams.get('utm_campaign'),
      60
    );
  }

  readonly canGoPrev = computed(
    () => this.loop || this.index() > 0
  );

  readonly canGoNext = computed(
    () => this.loop || this.index() < this.sortedOffers().length - 1
  );

  goTo(i: number): void {
    if (!this.sortedOffers().length) return;

    let newIndex: number;
    if (this.loop) {
      const len = this.sortedOffers().length;
      newIndex = ((i % len) + len) % len;
    } else {
      newIndex = Math.min(Math.max(i, 0), this.sortedOffers().length - 1);
    }

    this.index.set(newIndex);
    this.selectCurrentOffer(newIndex);
  }

  next(): void {
    this.goTo(this.index() + 1);
  }

  prev(): void {
    this.goTo(this.index() - 1);
  }

  @HostListener('window:scroll') onScrollEvent() {
    this.updateScrolled();
  }

  private waitForElementToRender = (selector: string) => {
    return new Promise(resolve => {
        // If it's already in DOM, resolve immediately
        if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
        }
        // Add an observer to resolve this Promise when the selector is found in the DOM
        const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
            observer.disconnect();
            resolve(document.querySelector(selector));
        }
        });
        observer.observe(document.documentElement, {
        childList: true,
        subtree: true
        });
    });
}

  // Sadly we cannot just update the z-index of Offers and CSW/Hub content to be above the ZenDesk launcher...
  // If we do that, we get offers overlapping with the chat window when opened.
  // It's a bad idea to irritate the user further with overlapping content when they're already looking for help lol.
  private async updateZenDeskLauncherPosition(scrolled: boolean) {
    if (!this.isStickyOfferBannerEnabled()) {
      return;
    }

    // If banner is dismissed, always reset position regardless of scroll state. Just in case the user scrolls first and then dismisses.
    if (this.isBannerDismissed()) {
      scrolled = false;
    }

    // We're making a few assumptions here (that are currently true...):
    // 1. ZenDesk continues to use an iframe with the id #launcher to display the chatbot button
    // 2. The ZenDesk #launcher iframe is a sibling of other chatbot positioned elements
    // 3. ZenDesk uses bottom styles to position chatbot elements (as opposed to top styles or other positioning methods)
    // 4. ZenDesk uses pixels to set bottom styles (as opposed to percentages or other units like 'rem')
    const launcherIframe = await this.waitForElementToRender('[id="launcher" i]') as HTMLElement; // assumption 1
    const parentDiv = launcherIframe.parentElement;
    if (parentDiv) {
      // Get all HTML element children of the parent div (AKA siblings of the launcher iframe - assumption 2), excluding style and script elements
      const children = Array.from(parentDiv.children).filter(
        (child): child is HTMLElement =>
          child instanceof HTMLElement &&
          child.tagName.toLowerCase() !== 'style' &&
          child.tagName.toLowerCase() !== 'script'
      );

      children.forEach((child: HTMLElement) => {
        const computedStyle = window.getComputedStyle(child);
        const inlineBottom = child.style.bottom;
        const computedBottom = computedStyle.bottom;

        // Check if element has a bottom style (either inline or computed, and not auto/initial/none)
        const hasBottomStyle = (inlineBottom && inlineBottom !== 'auto' && inlineBottom !== 'initial') ||
                               (computedBottom && computedBottom !== 'auto' && computedBottom !== 'initial' && computedBottom !== 'none');

        if (!hasBottomStyle) {
          // If elements don't have a bottom style then we shouldn't need to adjust their position - assumption 3
          return;
        }

        // Store original bottom value if not already stored
        if (!this.parentDivChildrenOriginalBottom.has(child)) {
          const originalBottom = inlineBottom || computedBottom || '0px';
          this.parentDivChildrenOriginalBottom.set(child, originalBottom);
        }

        const originalBottom = this.parentDivChildrenOriginalBottom.get(child) || '0px';

        if (scrolled) {
          const numericValueMatches = originalBottom.match(/\d+\.?\d*/);
          if (numericValueMatches) {
            const originalValue = parseFloat(numericValueMatches[0]);
            const newValue = originalValue + ZEN_DESK_LAUNCHER_ADJUSTMENT;
            child.style.bottom = `${newValue}px`;
          }
        } else {
          child.style.bottom = originalBottom;
        }

      });
    }
  }

  private updateScrolled() {
    const scrollTop = document.documentElement.scrollTop;
    this.scrolled = scrollTop > DISPLAY_MOBILE_TOP_POSITION;
    this.direction = scrollTop < 100 ? 'down' : 'up';
    this.updateZenDeskLauncherPosition(this.scrolled);
  }

  onCloseOfferClick() {
    this.closeBanner.emit(true);
  }

  onApply() {
    this.offersService.trackLinkClickEvent({
      offer: this.currentOffer,
      placement: '_CAROUSEL',
      siteSection: 'hub',
      cid: this.cid(),
      displayTypeSuffix: this.stickyBannerEvent || undefined,
      fsEvent: '[Auth] Customer Hub 2nd loan offer clicked',
    });
    this.windowRefService.nativeWindow.open(this.currentOffer.offerUrl, '_blank');
  }

  onDisclosureClick() {
    if (this.currentOffer?.offerType !== OfferType.RESUME_APPLICATION) {
      this.offersService.openDisclosures(this.currentOffer.subProductType);
      return;
    }
    if (this.currentOffer?.offerType === OfferType.RESUME_APPLICATION) {
      this.clickResumeApplication();
    }
  }

  clickResumeApplication() {
    this.offersService.trackLinkClickEvent({
      offer: this.currentOffer,
      placement: '_CAROUSEL',
      siteSection: 'hub',
      cid: this.cid(),
      displayTypeSuffix: this.stickyBannerEvent || undefined,
      fsEvent: '[Auth] Customer Hub 2nd resumable app link clicked',
    });
    this.windowRefService.nativeWindow.open(this.currentOffer.offerUrl, '_blank');
  }

  selectCurrentOffer(index: number) {
    this.currentOffer = this.sortedOffers()[index];
    this.carouselDisplayIndex = index;
    this.setOfferSectionTitle();
    this.emitOfferSeenEvent();
  }

  setOfferSectionTitle() {
    if (this.currentOffer?.offerType === OfferType.RESUME_APPLICATION) {
      this.offersSectionTitle = `Come back!`;
    } else {
      this.offersSectionTitle = `You're eligible`;
    }
  }

  sortRefiOffersFirst(offers: Offer[]): Offer[] {
    const refiOffers = offers.filter((offer) => isRefiOfferType(offer.offerType));
    const otherOffers = offers.filter((offer) => !isRefiOfferType(offer.offerType));
    return [...refiOffers, ...otherOffers];
  }


  dropToCookie(header: string, value: string, minutes: number) {
    const currentDate = new Date();
    currentDate.setTime(currentDate.getTime() + 60 * 1000 * minutes);
    this.cookieService.delete(header);
    this.cookieService.set(header, value, currentDate, '/', 'bestegg.com', location.hostname !== 'localhost', 'Strict');
  }

  emitOfferSeenEvent() {
    this.offersService.trackSeenEvent({
      offer: this.currentOffer,
      placement: '_CAROUSEL',
      siteSection: 'hub',
      cid: this.cid(),
      displayTypeSuffix: this.stickyBannerEvent || undefined,
    });
    if (isSecondLoanOrIta(this.currentOffer)) {
      const EVENT = '[Auth] Customer Hub 2nd loan offer seen';
      this.windowRefService.nativeWindow.FS.event(EVENT);
    } else if (this.currentOffer?.offerType === OfferType.RESUME_APPLICATION) {
      const EVENT = '[Auth] Customer Hub 2nd resumable app link seen';
      this.windowRefService.nativeWindow.FS.event(EVENT);
    }
  }

  getTranslationValue() {
    const offsetPercentage = this.carouselDisplayIndex * 100;
    const marginOffset = this.carouselDisplayIndex * 0.5;
    return `translateX(calc(-1 * (${offsetPercentage}% + ${marginOffset}rem)))`;
  }

  handleSwipe(e: TouchEvent): void {
    if (e.type === 'touchstart') {
      this.swipeXStart = e.changedTouches[0].clientX;
      return;
    }

    if (e.type === 'touchend') {
      const swipeXEnd = e.changedTouches[0].clientX;
      const coordinateDelta = Math.abs(swipeXEnd - this.swipeXStart);
      const minSwipeDistance = 50;

      if (coordinateDelta > minSwipeDistance) {
        if (swipeXEnd < this.swipeXStart) {
          this.next();
        } else {
          this.prev();
        }
      }
    }
  }

  handleLearnMoreClick(offer: Offer) {
    this.eventTrackingService.trackEvent<LinkClickCustomerPortal>(AuthEventType.LINK_CLICK, {
      eventRepo: AuthEventRepo.CustomerPortal,
      customerPortalClickLinkText: 'Learn more',
      customerPortalClickLinkType: AuthLinkType.OPEN_HSL_SLIDE_OUT,
    });
    this.learnMore.emit(offer);
  }
}
